import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Button, Checkbox } from '@mui/material';
import { Card, Autocomplete, TextField } from '@totalsoft/rocket-ui';
import GroupIcon from '@mui/icons-material/Group';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_USER_GROUPS_WRITE } from 'constants/rightsCodes';
import { emptyString } from 'utils/constants';
import { get, set } from '@totalsoft/change-tracking-react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

const UserGroupGeneralDataComponent = ({ lens, applications, isDirty, saving, onSave, validation }) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_USER_GROUPS_WRITE);
  const selectedApplications = applications.filter(app => {
    const appCodes = get(lens.appCodes);
    return Array.isArray(appCodes) && appCodes.includes(app.appCode);
  });
  const showIsAdmin = false;

  return (
    <Card icon={GroupIcon} iconColor='primary' title={t('UserGroups.GeneralData')}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} xl={4}>
          <TextField
            required
            fullWidth
            value={get(lens.name) || emptyString}
            onChange={event => set(lens.name)(event)}
            label={t('UserGroups.Name')}
            disabled={!canWrite}
            error={!isValid(validation.name)}
            helperText={getErrors(validation.name)}
          />
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          <TextField
            required
            fullWidth
            value={get(lens.code) || emptyString}
            onChange={event => set(lens.code)(event)}
            label={t('UserGroups.Code')}
            disabled={!canWrite}
            error={!isValid(validation.code)}
            helperText={getErrors(validation.code)}
          />
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          <TextField
            fullWidth
            value={get(lens.email) || emptyString}
            onChange={event => set(lens.email)(event)}
            label={t('UserGroups.Email')}
            disabled={!canWrite}
            error={!isValid(validation.email)}
            helperText={getErrors(validation.email)}
          />
        </Grid>
        <Grid item xs={12} xl={4}>
          <Autocomplete
            fullWidth
            value={selectedApplications}
            valueKey='appCode'
            labelKey='appName'
            isMultiSelection={true}
            options={applications}
            label={t('UserGroups.Applications')}
            onChange={value => set(lens.appCodes)(value.map(option => option.appCode))}
            isSearchable={true}
            isClearable={true}
            disabled={!canWrite}
          />
        </Grid>
        {showIsAdmin && (
          <Grid item xs={12} md={6} xl={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={get(lens.isAdmin) || false}
                  onClick={event => (lens.isAdmin |> set)(event.target.checked)}
                  disabled={!canWrite}
                />
              }
              label={t('UserGroups.IsAdmin')}
              labelPlacement='end'
            />
          </Grid>
        )}
      </Grid>
      <Grid container item xs={12} pt={2} justifyContent='flex-end'>
        <Button color='primary' variant='contained' disabled={!isDirty || !canWrite || saving} onClick={onSave}>
          {saving ? t('Saving') : t('Save')}
        </Button>
      </Grid>
    </Card>
  );
};

UserGroupGeneralDataComponent.propTypes = {
  lens: PropTypes.object.isRequired,
  applications: PropTypes.array,
  isDirty: PropTypes.bool,
  saving: PropTypes.bool,
  onSave: PropTypes.func,
  validation: PropTypes.object
};

export default UserGroupGeneralDataComponent;
